import React from 'react';

import { TextButton } from '@xingternal/button';
import { IconArrowDown, IconArrowUp } from '@xingternal/icons';
import { usePopOver } from '@xingternal/pop-over';
import { BodyCopy } from '@xingternal/typography';

import { LimitedJobCount } from '@/domain/search/components/limited-job-count';
import { useSearchQuery } from '@/domain/search/contexts';
import { FilterResult } from '@/domain/search/helpers';
import { useSearch } from '@/domain/search/hooks';
import {
  BenefitAggregationFragment,
  CareerLevelAggregationFragment,
  CityAggregationFragment,
  CountryAggregationFragment,
  DisciplineAggregationFragment,
  EmploymentTypeAggregationFragment,
  IndustryAggregationFragment,
  RemoteOptionAggregationFragment,
} from '@/graphql/generated';
import { useBreakpoints } from '@/hooks';

import * as Common from '../filter-list.common.styles';
import * as S from './filter-set.styles';

type Option =
  | RemoteOptionAggregationFragment
  | EmploymentTypeAggregationFragment
  | CareerLevelAggregationFragment
  | DisciplineAggregationFragment
  | IndustryAggregationFragment
  | BenefitAggregationFragment
  | CountryAggregationFragment
  | CityAggregationFragment
  | null
  | undefined;

type WithCountOptions<T> = T extends { count: number } ? T : never;
type CountOption = WithCountOptions<Option>;

type Props = {
  options?: Option[] | null;
  title: React.ReactNode;
  paramName: FilterResult['paramName'];
};

const countOrNull = (option: Exclude<Option, undefined | null>): React.ReactNode =>
  option.__typename !== 'JobAggregationRemoteOption' ? (
    <>
      (<LimitedJobCount count={(option as CountOption).count} />)
    </>
  ) : null;

export const FilterSet: React.FC<Props> = ({ options, title, paramName }) => {
  const {
    queryParams: [{ [paramName]: params }, setQueryParams],
  } = useSearchQuery();
  const { loading } = useSearch();
  const popOver = usePopOver();
  const { isMobile } = useBreakpoints();

  const onChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    const { checked, value } = event.target;
    const currentFilters = params ?? [];

    setQueryParams({
      [paramName]: checked ? [...currentFilters, value] : currentFilters.filter((item) => item !== value),
    });
  };

  const showNewFlag = paramName === 'remoteOption';

  return (
    <Common.FilterContainer>
      <TextButton
        iconAfter
        icon={popOver.show ? IconArrowUp : IconArrowDown}
        innerRef={popOver.triggerRef}
        onClick={popOver.togglePopOver}
        disabled={!options || options.length === 0}
      >
        {showNewFlag ? <S.Flag size="medium" variant="new" /> : null}
        {title}
      </TextButton>
      <S.Menu
        isInFlow
        onOutsideClick={popOver.handleHide}
        triggerRef={popOver.triggerRef}
        show={popOver.show}
        showCloseButton={false}
      >
        {isMobile ? (
          <>
            <S.BottomSheetTitle size="small">{title}</S.BottomSheetTitle>
            <S.Divider />
          </>
        ) : null}
        {options?.map((option) =>
          option?.[paramName]?.localizationValue ? (
            <Common.Label key={option.id}>
              <Common.Checkbox
                value={option.id}
                onChange={onChange}
                on={!!params?.includes(option.id)}
                disabled={loading}
              />
              <BodyCopy size="medium" noMargin fontWeight={params?.includes(option.id) ? 'bold' : 'regular'}>
                {option[paramName].localizationValue} {countOrNull(option)}
              </BodyCopy>
            </Common.Label>
          ) : null
        )}
      </S.Menu>
    </Common.FilterContainer>
  );
};
